import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  MonitorOptions,
  TrackingErrorProperties,
  TrackingEventProperties,
  TrackingPageViewProperties
} from '../../models/analytic/monitor-service-properties.model';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { AzureAppInsightsService } from '../azure-app-insights/azure-app-insights.service';
import { HeapService } from '../heap/heap.service';

@Injectable({
  providedIn: 'root'
})
export class MonitorService {
  private defaultProperties: { [key: string]: string };

  constructor(
    private appSettingsService: AppSettingsService,
    private appInsightsService: AzureAppInsightsService,
    private heapService: HeapService
  ) { }

  private get isAppInsightsEnabled(): boolean {
    return (
      !!this.appSettingsService.configuration &&
      !!this.appSettingsService.configuration.appInsights &&
      !!this.appSettingsService.configuration.appInsights.instrumentationKey &&
      this.appSettingsService.configuration.monitorSettings.isEnabled
    );
  }

  public logPageView(
    properties: TrackingPageViewProperties,
    monitorOptions: MonitorOptions = {
      sendToAppInsight: true,
      sendToHeap: true
    }
  ): void {
    if (monitorOptions.sendToAppInsight && this.isAppInsightsEnabled) {
      this.appInsightsService.trackPageView({
        name: properties.name,
        uri: properties.uri,
        measurements: properties.measurements || undefined,
        properties: {
          ...this.defaultProperties,
          ...properties.properties,
          timestamp: new Date(),
          duration: properties.duration
        }
      });
    }

    if (monitorOptions.sendToHeap) {
      this.heapService.track(properties.name, {
        ...properties.properties,
        timestamp: new Date()
      });
    }
  }

  public logEvent(
    properties: TrackingEventProperties,
    monitorOptions: MonitorOptions = {
      sendToAppInsight: true,
      sendToHeap: true
    }
  ): void {
    if (monitorOptions.sendToAppInsight && this.isAppInsightsEnabled) {
      console.log('send to app insight', properties.name);
      this.appInsightsService.trackEvent({
        name: properties.name,
        measurements: properties.measurements || undefined,
        properties: {
          ...this.defaultProperties,
          ...properties.properties
        }
      });
    }

    if (monitorOptions.sendToHeap) {
      console.log('send to heap', properties.name);
      this.heapService.track(properties.name, {
        ...properties.properties,
        timestamp: new Date()
      });
    }
  }

  public logError(
    properties: TrackingErrorProperties,
    monitorOptions: MonitorOptions = {
      sendToAppInsight: true,
      sendToHeap: false
    }
  ): void {
    if (monitorOptions.sendToAppInsight && this.isAppInsightsEnabled) {
      this.appInsightsService.trackException({
        error: properties.error,
        properties: {
          ...this.defaultProperties,
          ...properties.properties
        },
        measurements: properties.measurements
      });
    }

    if (monitorOptions.sendToHeap) {
      this.heapService.track(properties.error.name, {
        ...properties.error,
        ...properties.properties,
        timestamp: new Date()
      });
    }
  }

  public setDefaultProperties(): Observable<void> {
    return this.appSettingsService.appSettings$.pipe(
      map((settings) => {
        this.defaultProperties = {
          buildNumber: settings.buildNumber
        };
      })
    );
  }
}
